import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import AppHeader from "../components/AppHeader.js";
import ScrollToTopButton from "../components/ScrollToTopButton.js";
import {
  GET,
  loading,
  Select,
  DatePicker,
  Pagination,
  toFixed,
  format_date,
  XLSX,
  saveAs,
} from "../components/CustomComponent.js";
import "../assets/css/bootstrap.css";
import "../assets/css/style.css";
import "../assets/css/manora-report.css";

const PAGE_SIZE = 100000;
// สถานะใบเบิกที่ให้เลือกในตัวกรอง — เทียบด้วย title ไม่ใช่ id (id จริงต่างกันได้ในแต่ละ environment
// เหมือนที่ฝั่ง backend resolve จาก setting_status.title ตอน runtime)
const WITHDRAWAL_STATUS_TITLES = ["Waiting Dispatch", "Dispatched", "Received"];

// ใช้ปีคริสต์ศักราชแบบตรงๆ (ไม่ผ่าน format_date เพราะ format_date จะบวก +543 ให้เป็น พ.ศ. โดย default)
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TODAY = toISODate(new Date());
// (WEB-260826-3.1) ค่าเริ่มต้นของช่วงวันรายงานใบเบิก = ย้อนหลัง 30 วัน ให้ตรงกับ default ฝั่ง backend
// (report.service.js#parseReportDateRange ใช้ today.subtract(29, "day")) ไม่งั้น from=to=วันนี้ พอไม่มีใบเบิกวันนี้ก็ว่างเปล่า
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};
const WITHDRAWAL_DEFAULT_FROM = daysAgo(29);

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined));

const movementTypeBadge = (t, type) => {
  const map = {
    receive: { label: t("report.type_receive"), cls: "manora-badge-green" },
    dispatch: { label: t("report.type_dispatch"), cls: "manora-badge-red" },
    produce: { label: t("report.type_produce"), cls: "manora-badge-purple" },
    transfer: { label: t("report.type_transfer"), cls: "manora-badge-blue" },
    // (WEB-260828-5) ของคืนจากทอด/บรรจุ — แยก badge ตามสถานีต้นทาง
    return_fry: { label: t("report.type_return_fry"), cls: "manora-badge-orange" },
    return_pack: { label: t("report.type_return_pack"), cls: "manora-badge-orange" },
  };
  return map[type] || { label: type, cls: "manora-badge-gray" };
};

const withdrawalStatusBadge = (t, title) => {
  const map = {
    Received: { label: t("report.status_received"), cls: "manora-badge-green" },
    Dispatched: { label: t("report.status_dispatched"), cls: "manora-badge-blue" },
    "Waiting Dispatch": { label: t("report.status_waiting"), cls: "manora-badge-orange" },
  };
  // (WEB-260901-1) ไม่มีสถานะ = ยังไม่ถูกจ่าย ให้ตกมาที่ "รอส่ง" เหมือนหน้าใบเบิกบน mobile ไม่โชว์ "-"
  return map[title] || map["Waiting Dispatch"];
};

// สถานะของรายงานใบนำวัตถุดิบ (production_send) มีแค่ 2 ค่า ไม่ผูกกับ setting_status
// (WEB-260915-1) ดู SEND_STATUS_KEYS ฝั่ง report.service.js — key ต้องตรงกันเป๊ะ
const SEND_STATUS_TITLES = ["WAITING_RECEIVE", "RECEIVED"];
const sendStatusBadge = (t, key) => {
  const map = {
    RECEIVED: { label: t("report.status_received"), cls: "manora-badge-green" },
    WAITING_RECEIVE: { label: t("report.status_waiting_receive"), cls: "manora-badge-orange" },
  };
  return map[key] || map.WAITING_RECEIVE;
};

class Reports extends Component {
  state = {
    activeSubTab: "movement",

    products: [],
    warehouses: [],
    statuses: [],

    movementFilters: { from: TODAY, to: TODAY, product_id: "", warehouse_id: "", type: "", batch_no: "", pallet_no: "" },
    movementPage: 1,
    movementSummary: { receive_total: 0, receive_count: 0, dispatch_total: 0, dispatch_count: 0, net_change: 0, total_count: 0 },
    movementItems: [],
    movementPagination: null,
    movementFrom: "",
    movementTo: "",

    withdrawalFilters: { from: WITHDRAWAL_DEFAULT_FROM, to: TODAY, product_id: "", status_id: "", data_search: "" },
    withdrawalPage: 1,
    withdrawalSummary: { total_docs: 0, waiting_count: 0, dispatched_count: 0, received_count: 0, total_dispatched_amount: 0 },
    withdrawalItems: [],
    withdrawalPagination: null,
    withdrawalFrom: "",
    withdrawalTo: "",
    withdrawalLoaded: false,

    sendFilters: { from: WITHDRAWAL_DEFAULT_FROM, to: TODAY, product_id: "", status: "", data_search: "" },
    sendPage: 1,
    sendSummary: { total_docs: 0, waiting_count: 0, received_count: 0, total_send_amount: 0 },
    sendItems: [],
    sendPagination: null,
    sendFrom: "",
    sendTo: "",
    sendLoaded: false,
  };

  async componentDidMount() {
    loading(true);
    const [productsRes, warehousesRes, statusesRes] = await Promise.all([
      GET("/api/v1/internal/product", { page: 0 }),
      GET("/api/v1/internal/warehouse", { page: 0 }),
      GET("/api/v1/internal/status", { page: 0 }),
    ]);
    this.setState({
      products: productsRes?.status === true ? productsRes.data : [],
      warehouses: warehousesRes?.status === true ? warehousesRes.data : [],
      statuses:
        statusesRes?.status === true
          ? statusesRes.data.filter((s) => WITHDRAWAL_STATUS_TITLES.includes(s.title))
          : [],
    });
    await this.GetMovement(1);
    loading(false);
  }

  GetMovement = async (page) => {
    loading(true);
    const params = cleanParams({ ...this.state.movementFilters, page, page_size: PAGE_SIZE });
    const result = await GET("/api/v1/internal/report/movement", params);
    if (result?.status === true) {
      this.setState({
        movementPage: page,
        movementSummary: result.data.summary,
        movementItems: result.data.items,
        movementPagination: result.pagination,
        movementFrom: result.data.from,
        movementTo: result.data.to,
      });
    }
    loading(false);
  };

  GetWithdrawal = async (page) => {
    loading(true);
    const params = cleanParams({ ...this.state.withdrawalFilters, page, page_size: PAGE_SIZE });
    const result = await GET("/api/v1/internal/report/withdrawal", params);
    if (result?.status === true) {
      this.setState({
        withdrawalPage: page,
        withdrawalSummary: result.data.summary,
        withdrawalItems: result.data.items,
        withdrawalPagination: result.pagination,
        withdrawalFrom: result.data.from,
        withdrawalTo: result.data.to,
        withdrawalLoaded: true,
      });
    }
    loading(false);
  };

  GetSend = async (page) => {
    loading(true);
    const params = cleanParams({ ...this.state.sendFilters, page, page_size: PAGE_SIZE });
    const result = await GET("/api/v1/internal/report/production-send", params);
    if (result?.status === true) {
      this.setState({
        sendPage: page,
        sendSummary: result.data.summary,
        sendItems: result.data.items,
        sendPagination: result.pagination,
        sendFrom: result.data.from,
        sendTo: result.data.to,
        sendLoaded: true,
      });
    }
    loading(false);
  };

  updateMovementFilter = (key, value) => {
    this.setState({ movementFilters: { ...this.state.movementFilters, [key]: value } }, () => this.GetMovement(1));
  };

  updateWithdrawalFilter = (key, value) => {
    this.setState({ withdrawalFilters: { ...this.state.withdrawalFilters, [key]: value } }, () => this.GetWithdrawal(1));
  };

  updateSendFilter = (key, value) => {
    this.setState({ sendFilters: { ...this.state.sendFilters, [key]: value } }, () => this.GetSend(1));
  };

  switchSubTab = (tab) => {
    this.setState({ activeSubTab: tab });
    if (tab === "withdrawal" && !this.state.withdrawalLoaded) {
      this.GetWithdrawal(1);
    }
    if (tab === "send" && !this.state.sendLoaded) {
      this.GetSend(1);
    }
  };

  ExportMovementExcel = async () => {
    loading(true);
    const params = cleanParams({ ...this.state.movementFilters, page: 0 });
    const result = await GET("/api/v1/internal/report/movement", params);
    if (result?.status === true) {
      const { t } = this.props;
      const rows = result.data.items.map((item) => ({
        วันที่: item.event_date,
        เวลา: item.event_time,
        ประเภท: movementTypeBadge(t, item.movement_type).label,
        รหัสสินค้า: item.product?.code || "",
        รายละเอียดสินค้า: item.product?.name || "",
        Batch: item.batch_no || "",
        อ้างอิง: item.ref_no || "",
        Pallet: item.pallet_no || "",
        ต้นทาง: item.from_location || "",
        ปลายทาง: item.to_location || "",
        "รับเข้า (KG)": item.receive_amount ?? "",
        "จ่ายออก (KG)": item.dispatch_amount ?? "",
        หมายเหตุ: item.remark || "",
        ผู้ทำรายการ: item.user_name || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movement");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `movement-${format_date(new Date(), "yyyy-mm-dd")}.xlsx`);
    }
    loading(false);
  };

  ExportWithdrawalExcel = async () => {
    loading(true);
    const params = cleanParams({ ...this.state.withdrawalFilters, page: 0 });
    const result = await GET("/api/v1/internal/report/withdrawal", params);
    if (result?.status === true) {
      const rows = result.data.items.map((item) => ({
        เลขที่เอกสาร: item.req_no || "",
        วันที่: item.req_date,
        เวลา: item.req_time,
        ผู้เบิก: item.requester_name || "",
        แผนก: item.department_name || "",
        รหัสสินค้า: item.product?.code || "",
        รายละเอียดสินค้า: item.product?.name || "",
        Batch: item.batch_no || "",
        คลัง: item.warehouse?.name || "",
        จำนวนที่เบิก: item.requested_amount,
        จำนวนที่จ่าย: item.dispatched_amount,
        สถานะ: withdrawalStatusBadge(this.props.t, item.status?.title).label,
        หมายเหตุ: item.remark || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Withdrawal");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `withdrawal-${format_date(new Date(), "yyyy-mm-dd")}.xlsx`);
    }
    loading(false);
  };

  ExportSendExcel = async () => {
    loading(true);
    const params = cleanParams({ ...this.state.sendFilters, page: 0 });
    const result = await GET("/api/v1/internal/report/production-send", params);
    if (result?.status === true) {
      const { t } = this.props;
      const rows = result.data.items.map((item) => ({
        เลขที่เอกสาร: item.send_no || "",
        วันที่: item.send_date,
        เวลา: item.send_time,
        ผู้ส่ง: item.sender_name || "",
        แผนก: item.department_name || "",
        รหัสสินค้า: item.product?.code || "",
        รายละเอียดสินค้า: item.product?.name || "",
        Batch: item.batch_no || "",
        Pallet: item.pallet_no || "",
        คลัง: item.warehouse?.name || "",
        จำนวนที่ส่ง: item.amount,
        สถานะ: sendStatusBadge(t, item.status?.key).label,
        หมายเหตุ: item.remark || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ProductionSend");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `production-send-${format_date(new Date(), "yyyy-mm-dd")}.xlsx`);
    }
    loading(false);
  };

  renderMovement() {
    const { movementFilters, movementSummary, movementItems, movementPagination, movementPage, movementFrom, movementTo, products, warehouses } =
      this.state;
    const { t } = this.props;

    return (
      <>
        <div className="manora-filter-card d-flex flex-wrap align-items-end mb-3" style={{ gap: 10 }}>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.from_date")}</div>
            <DatePicker className="form-control" value={movementFilters.from} onChange={(v) => this.updateMovementFilter("from", v)} />
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.to_date")}</div>
            <DatePicker className="form-control" value={movementFilters.to} onChange={(v) => this.updateMovementFilter("to", v)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_product")}</div>
            <Select
              className="form-control"
              options={products.map((p) => ({ value: p.id, label: p.code ? `${p.code} - ${p.alias || p.name}` : p.alias || p.name }))}
              value={movementFilters.product_id}
              onChange={(v) => this.updateMovementFilter("product_id", v)}
            />
          </div>
          <div style={{ minWidth: 140 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_warehouse")}</div>
            <Select
              className="form-control"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              value={movementFilters.warehouse_id}
              onChange={(v) => this.updateMovementFilter("warehouse_id", v)}
            />
          </div>
          <div style={{ minWidth: 140 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_type")}</div>
            <Select
              className="form-control"
              options={[
                { value: "receive", label: t("report.type_receive") },
                { value: "dispatch", label: t("report.type_dispatch") },
                { value: "transfer", label: t("report.type_transfer") },
                { value: "return", label: t("report.type_return") },
              ]}
              value={movementFilters.type}
              onChange={(v) => this.updateMovementFilter("type", v)}
            />
          </div>
          <div style={{ minWidth: 140 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_batch")}</div>
            <input
              className="form-control"
              placeholder={t("report.filter_batch_placeholder")}
              value={movementFilters.batch_no}
              onChange={(e) => this.updateMovementFilter("batch_no", e.target.value)}
            />
          </div>
          {/* (TpUEX76X) ค้นด้วยเลขพาเลท (บางส่วนก็ได้) — backend กรองทุกประเภทรายการด้วย item.pallet_no */}
          <div style={{ minWidth: 160 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_pallet")}</div>
            <input
              className="form-control"
              placeholder={t("report.filter_pallet_placeholder")}
              value={movementFilters.pallet_no}
              onChange={(e) => this.updateMovementFilter("pallet_no", e.target.value)}
            />
          </div>
          <button className="btn btn-success" onClick={() => this.ExportMovementExcel()}>
            {t("report.export_excel")}
          </button>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-green h-100">
              <div className="manora-summary-label">{t("report.movement_receive_total")}</div>
              <div className="manora-summary-value">{toFixed(movementSummary.receive_total)} KG</div>
              <div className="manora-summary-sub">
                {movementSummary.receive_count} {t("report.unit_item")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-red h-100">
              <div className="manora-summary-label">{t("report.movement_dispatch_total")}</div>
              <div className="manora-summary-value">{toFixed(movementSummary.dispatch_total)} KG</div>
              <div className="manora-summary-sub">
                {movementSummary.dispatch_count} {t("report.unit_item")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-purple h-100">
              <div className="manora-summary-label">{t("report.movement_net_change")}</div>
              <div className="manora-summary-value">
                {movementSummary.net_change >= 0 ? "+" : ""}
                {toFixed(movementSummary.net_change)} KG
              </div>
              <div className="manora-summary-sub">{t("report.movement_net_change_sub")}</div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-orange h-100">
              <div className="manora-summary-label">{t("report.movement_total_count")}</div>
              <div className="manora-summary-value">
                {movementSummary.total_count} {t("report.unit_item")}
              </div>
            </div>
          </div>
        </div>

        <div className="manora-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">
              {t("report.list_title_movement")} ({movementFrom ? format_date(movementFrom) : "-"} — {movementTo ? format_date(movementTo) : "-"})
            </div>
            <small className="text-secondary">
              {movementSummary.total_count} {t("report.unit_item")}
            </small>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <td className="text-nowrap">{t("report.col_date")}</td>
                  <td className="text-nowrap">{t("report.col_time")}</td>
                  <td className="text-nowrap">{t("report.col_type")}</td>
                  <td className="text-nowrap">{t("report.col_product_code")}</td>
                  <td className="text-nowrap">{t("report.col_product_detail")}</td>
                  <td className="text-nowrap">{t("report.col_batch_pallet")}</td>
                  <td className="text-nowrap">{t("report.col_from")}</td>
                  <td className="text-nowrap">{t("report.col_to")}</td>
                  <td className="text-nowrap text-end">{t("report.col_receive_kg")}</td>
                  <td className="text-nowrap text-end">{t("report.col_dispatch_kg")}</td>
                  <td className="text-nowrap">{t("report.col_remark")}</td>
                  <td className="text-nowrap">{t("report.col_user")}</td>
                </tr>
              </thead>
              <tbody>
                {movementItems.map((item, i) => {
                  const badge = movementTypeBadge(t, item.movement_type);
                  return (
                    <tr key={`mv-${i}`}>
                      <td className="text-nowrap">{item.event_date ? format_date(item.event_date) : "-"}</td>
                      <td className="text-nowrap">{item.event_time}</td>
                      <td className="text-nowrap">
                        <span className={`manora-badge ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="text-nowrap">{item.product?.code}</td>
                      <td className="text-nowrap">
                        <div>{item.product?.name}</div>
                        <div className="text-12 text-secondary">{item.product?.subtext}</div>
                      </td>
                      <td className="text-nowrap">
                        <div className="manora-link-text">{item.batch_no}</div>
                        <div className="text-12 text-secondary">{item.ref_no}</div>
                        {/* แถวรับเข้า ref_no เป็นเลขใบรับ — โชว์เลขพาเลทเพิ่มให้เห็นว่าค้นเจอพาเลทไหน */}
                        {item.pallet_no && item.pallet_no !== item.ref_no && (
                          <div className="text-12 text-secondary">{item.pallet_no}</div>
                        )}
                      </td>
                      <td className="text-nowrap">{item.from_location || "-"}</td>
                      <td className="text-nowrap">{item.to_location || "-"}</td>
                      <td className="text-nowrap text-end text-success fw-bold">{item.receive_amount != null ? toFixed(item.receive_amount) : "—"}</td>
                      <td className="text-nowrap text-end text-danger fw-bold">{item.dispatch_amount != null ? toFixed(item.dispatch_amount) : "—"}</td>
                      {/* สาเหตุการย้าย (ย้ายตำแหน่งบังคับกรอก) */}
                      <td>{item.remark || "-"}</td>
                      <td className="text-nowrap">{item.user_name}</td>
                    </tr>
                  );
                })}
                {movementItems.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-secondary py-4">
                      {t("report.no_data")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-2">
            <span className="mb-3 me-3 text-secondary">
              {movementPagination
                ? t("report.pagination_showing", {
                    from: movementPagination.start_index + 1,
                    to: movementPagination.end_index,
                    total: movementPagination.count,
                  })
                : ""}
            </span>
            <Pagination
              className="mb-3"
              count={Math.ceil((movementPagination?.count || 0) / PAGE_SIZE) || 1}
              page={movementPage}
              showFirstButton
              showLastButton
              onChange={(_, page) => this.GetMovement(page)}
            />
          </div>
        </div>
      </>
    );
  }

  renderWithdrawal() {
    const {
      withdrawalFilters,
      withdrawalSummary,
      withdrawalItems,
      withdrawalPagination,
      withdrawalPage,
      withdrawalFrom,
      withdrawalTo,
      products,
      statuses,
    } = this.state;
    const { t } = this.props;

    return (
      <>
        <div className="manora-filter-card d-flex flex-wrap align-items-end mb-3" style={{ gap: 10 }}>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.from_date")}</div>
            <DatePicker className="form-control" value={withdrawalFilters.from} onChange={(v) => this.updateWithdrawalFilter("from", v)} />
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.to_date")}</div>
            <DatePicker className="form-control" value={withdrawalFilters.to} onChange={(v) => this.updateWithdrawalFilter("to", v)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_product")}</div>
            <Select
              className="form-control"
              options={products.map((p) => ({ value: p.id, label: p.code ? `${p.code} - ${p.alias || p.name}` : p.alias || p.name }))}
              value={withdrawalFilters.product_id}
              onChange={(v) => this.updateWithdrawalFilter("product_id", v)}
            />
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_document_status")}</div>
            <Select
              className="form-control"
              // label เป็นภาษาเดียวกับป้ายสถานะในตาราง ค้นด้วยคำอังกฤษเดิม (keywords) ได้ด้วย
              options={statuses.map((s) => ({
                value: s.id,
                label: withdrawalStatusBadge(t, s.title).label,
                keywords: s.title,
              }))}
              value={withdrawalFilters.status_id}
              onChange={(v) => this.updateWithdrawalFilter("status_id", v)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_document_search")}</div>
            <input
              className="form-control"
              placeholder={t("report.filter_document_search_placeholder")}
              value={withdrawalFilters.data_search}
              onChange={(e) => this.updateWithdrawalFilter("data_search", e.target.value)}
            />
          </div>
          <button className="btn btn-success" onClick={() => this.ExportWithdrawalExcel()}>
            {t("report.export_excel")}
          </button>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-purple h-100">
              <div className="manora-summary-label">{t("report.withdrawal_total_docs")}</div>
              <div className="manora-summary-value">
                {withdrawalSummary.total_docs} {t("report.withdrawal_unit_doc")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-orange h-100">
              <div className="manora-summary-label">{t("report.withdrawal_waiting_dispatched")}</div>
              <div className="manora-summary-value">
                {withdrawalSummary.waiting_count + withdrawalSummary.dispatched_count} {t("report.withdrawal_unit_doc")}
              </div>
              <div className="manora-summary-sub">
                {t("report.withdrawal_waiting_dispatched_sub", {
                  waiting: withdrawalSummary.waiting_count,
                  dispatched: withdrawalSummary.dispatched_count,
                })}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-green h-100">
              <div className="manora-summary-label">{t("report.withdrawal_received")}</div>
              <div className="manora-summary-value">
                {withdrawalSummary.received_count} {t("report.withdrawal_unit_doc")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-purple h-100">
              <div className="manora-summary-label">{t("report.withdrawal_total_amount")}</div>
              <div className="manora-summary-value">{toFixed(withdrawalSummary.total_dispatched_amount)} KG</div>
            </div>
          </div>
        </div>

        <div className="manora-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">
              {t("report.list_title_withdrawal")} ({withdrawalFrom ? format_date(withdrawalFrom) : "-"} — {withdrawalTo ? format_date(withdrawalTo) : "-"})
            </div>
            <small className="text-secondary">
              {/* จำนวนแถวในตาราง (ใบเบิก 1 ใบมีได้หลายรายการ — total_docs เป็นจำนวน "ใบ" ไม่ใช่ "รายการ") */}
              {withdrawalItems.length} {t("report.unit_item")}
            </small>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <td>{t("report.col_doc_no")}</td>
                  <td>{t("report.col_date")}</td>
                  <td>{t("report.col_time")}</td>
                  <td>{t("report.col_requester_dept")}</td>
                  <td>{t("report.col_product_code")}</td>
                  <td>{t("report.col_product_detail")}</td>
                  <td>{t("report.col_batch_pallet")}</td>
                  <td>{t("report.col_warehouse")}</td>
                  <td className="text-end">{t("report.col_requested_amount")}</td>
                  <td className="text-end">{t("report.col_dispatched_amount")}</td>
                  <td>{t("report.col_status")}</td>
                  <td>{t("report.col_remark")}</td>
                </tr>
              </thead>
              <tbody>
                {withdrawalItems.map((item, i) => {
                  const badge = withdrawalStatusBadge(t, item.status?.title);
                  return (
                    <tr key={`wd-${i}`}>
                      <td className="manora-link-text">{item.req_no}</td>
                      <td>{item.req_date ? format_date(item.req_date) : "-"}</td>
                      <td>{item.req_time}</td>
                      <td>
                        <div>{item.requester_name}</div>
                        <div className="text-12 text-secondary">{item.department_name}</div>
                      </td>
                      <td>{item.product?.code}</td>
                      <td>{item.product?.name}</td>
                      <td>
                        <div className="manora-link-text">{item.batch_no}</div>
                        <div className="text-12 text-secondary">{item.ref_code}</div>
                      </td>
                      <td>{item.warehouse?.name}</td>
                      <td className="text-end">{toFixed(item.requested_amount)}</td>
                      <td className="text-end">{toFixed(item.dispatched_amount)}</td>
                      <td>
                        <span className={`manora-badge ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td>{item.remark || "-"}</td>
                    </tr>
                  );
                })}
                {withdrawalItems.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-secondary py-4">
                      {t("report.no_data")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-2">
            <span className="mb-3 me-3 text-secondary">
              {withdrawalPagination
                ? t("report.pagination_showing", {
                    from: withdrawalPagination.start_index + 1,
                    to: withdrawalPagination.end_index,
                    total: withdrawalPagination.count,
                  })
                : ""}
            </span>
            <Pagination
              className="mb-3"
              count={Math.ceil((withdrawalPagination?.count || 0) / PAGE_SIZE) || 1}
              page={withdrawalPage}
              showFirstButton
              showLastButton
              onChange={(_, page) => this.GetWithdrawal(page)}
            />
          </div>
        </div>
      </>
    );
  }

  renderSend() {
    const { sendFilters, sendSummary, sendItems, sendPagination, sendPage, sendFrom, sendTo, products } = this.state;
    const { t } = this.props;

    return (
      <>
        <div className="manora-filter-card d-flex flex-wrap align-items-end mb-3" style={{ gap: 10 }}>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.from_date")}</div>
            <DatePicker className="form-control" value={sendFilters.from} onChange={(v) => this.updateSendFilter("from", v)} />
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.to_date")}</div>
            <DatePicker className="form-control" value={sendFilters.to} onChange={(v) => this.updateSendFilter("to", v)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_product")}</div>
            <Select
              className="form-control"
              options={products.map((p) => ({ value: p.id, label: p.code ? `${p.code} - ${p.alias || p.name}` : p.alias || p.name }))}
              value={sendFilters.product_id}
              onChange={(v) => this.updateSendFilter("product_id", v)}
            />
          </div>
          <div style={{ minWidth: 150 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_document_status")}</div>
            <Select
              className="form-control"
              // (WEB-260915-1) สถานะของรายงานนี้มีแค่ 2 ค่าคงที่ ไม่ต้องดึงจาก API เหมือนใบเบิก
              options={SEND_STATUS_TITLES.map((key) => ({
                value: key,
                label: sendStatusBadge(t, key).label,
                keywords: key,
              }))}
              value={sendFilters.status}
              onChange={(v) => this.updateSendFilter("status", v)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <div className="text-12 text-secondary mb-1">{t("report.filter_document_search")}</div>
            <input
              className="form-control"
              placeholder={t("report.filter_document_search_send_placeholder")}
              value={sendFilters.data_search}
              onChange={(e) => this.updateSendFilter("data_search", e.target.value)}
            />
          </div>
          <button className="btn btn-success" onClick={() => this.ExportSendExcel()}>
            {t("report.export_excel")}
          </button>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-purple h-100">
              <div className="manora-summary-label">{t("report.send_total_docs")}</div>
              <div className="manora-summary-value">
                {sendSummary.total_docs} {t("report.withdrawal_unit_doc")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-orange h-100">
              <div className="manora-summary-label">{t("report.send_waiting_count")}</div>
              <div className="manora-summary-value">
                {sendSummary.waiting_count} {t("report.withdrawal_unit_doc")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-green h-100">
              <div className="manora-summary-label">{t("report.send_received_count")}</div>
              <div className="manora-summary-value">
                {sendSummary.received_count} {t("report.withdrawal_unit_doc")}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="manora-summary-card border-purple h-100">
              <div className="manora-summary-label">{t("report.send_total_amount")}</div>
              <div className="manora-summary-value">{toFixed(sendSummary.total_send_amount)} KG</div>
            </div>
          </div>
        </div>

        <div className="manora-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">
              {t("report.list_title_send")} ({sendFrom ? format_date(sendFrom) : "-"} — {sendTo ? format_date(sendTo) : "-"})
            </div>
            <small className="text-secondary">
              {sendItems.length} {t("report.unit_item")}
            </small>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <td>{t("report.col_doc_no")}</td>
                  <td>{t("report.col_date")}</td>
                  <td>{t("report.col_time")}</td>
                  <td>{t("report.col_sender_dept")}</td>
                  <td>{t("report.col_product_code")}</td>
                  <td>{t("report.col_product_detail")}</td>
                  <td>{t("report.col_batch_pallet")}</td>
                  <td>{t("report.col_warehouse")}</td>
                  <td className="text-end">{t("report.col_send_amount")}</td>
                  <td>{t("report.col_status")}</td>
                  <td>{t("report.col_remark")}</td>
                </tr>
              </thead>
              <tbody>
                {sendItems.map((item, i) => {
                  const badge = sendStatusBadge(t, item.status?.key);
                  return (
                    <tr key={`sd-${i}`}>
                      <td className="manora-link-text">{item.send_no}</td>
                      <td>{item.send_date ? format_date(item.send_date) : "-"}</td>
                      <td>{item.send_time}</td>
                      <td>
                        <div>{item.sender_name}</div>
                        <div className="text-12 text-secondary">{item.department_name}</div>
                      </td>
                      <td>{item.product?.code}</td>
                      <td>{item.product?.name}</td>
                      <td>
                        <div className="manora-link-text">{item.batch_no}</div>
                        <div className="text-12 text-secondary">{item.pallet_no}</div>
                      </td>
                      <td>{item.warehouse?.name}</td>
                      <td className="text-end">{toFixed(item.amount)}</td>
                      <td>
                        <span className={`manora-badge ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td>{item.remark || "-"}</td>
                    </tr>
                  );
                })}
                {sendItems.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-secondary py-4">
                      {t("report.no_data")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-2">
            <span className="mb-3 me-3 text-secondary">
              {sendPagination
                ? t("report.pagination_showing", {
                    from: sendPagination.start_index + 1,
                    to: sendPagination.end_index,
                    total: sendPagination.count,
                  })
                : ""}
            </span>
            <Pagination
              className="mb-3"
              count={Math.ceil((sendPagination?.count || 0) / PAGE_SIZE) || 1}
              page={sendPage}
              showFirstButton
              showLastButton
              onChange={(_, page) => this.GetSend(page)}
            />
          </div>
        </div>
      </>
    );
  }

  render() {
    const { activeSubTab } = this.state;
    const { t } = this.props;
    return (
      <div className="w-100 min-vh-100" style={{ background: "#f5f3fa" }}>
        <AppHeader title={t("report.title")} subtitle={t("report.subtitle")} activeTab="reports" />

        <div className="p-3">
          <div className="d-flex mb-3" style={{ gap: 10 }}>
            <button
              type="button"
              className={`manora-tab-pill ${activeSubTab === "movement" ? "active" : ""}`}
              onClick={() => this.switchSubTab("movement")}
            >
              {t("report.movement_tab")}
            </button>
            <button
              type="button"
              className={`manora-tab-pill ${activeSubTab === "withdrawal" ? "active" : ""}`}
              onClick={() => this.switchSubTab("withdrawal")}
            >
              {t("report.withdrawal_tab")}
            </button>
            <button
              type="button"
              className={`manora-tab-pill ${activeSubTab === "send" ? "active" : ""}`}
              onClick={() => this.switchSubTab("send")}
            >
              {t("report.send_tab")}
            </button>
          </div>

          {activeSubTab === "movement" && this.renderMovement()}
          {activeSubTab === "withdrawal" && this.renderWithdrawal()}
          {activeSubTab === "send" && this.renderSend()}
        </div>
        <ScrollToTopButton />
      </div>
    );
  }
}

export default withTranslation()(Reports);
